/**
 * Form Configuration Loader
 * Loads and validates form configuration from API
 */

import { API } from '../utils/api.js';
import { Security } from './security.js';

export class FormConfig {
  /**
   * Load form configuration
   * @param {string} formId - Form UUID
   * @param {string} behalfToken - Optional behalf token
   * @returns {Promise<Object>}
   */
  static async load(formId, behalfToken = null) {
    if (!Security.validateFormId(formId)) {
      throw new Error('Invalid form ID');
    }

    try {
      const response = await API.loadFormConfig(formId, behalfToken);
      
      if (response.status !== 'ok' || !response.form) {
        // Preserve error type for access control errors
        const error = new Error(response.error || 'Failed to load form configuration');
        error.errorType = response.error; // Store error type (login_required, access_denied, etc.)
        throw error;
      }

      return response.form;
    } catch (error) {
      // Preserve error type if it exists
      if (error.errorType) {
        const newError = new Error(error.message);
        newError.errorType = error.errorType;
        throw newError;
      }
      throw new Error(`Error loading form configuration: ${error.message}`);
    }
  }

  /**
   * Load current user (IAM).
   * @param {string|function|null} jwtOrGetter - Optional JWT string or function returning JWT (for apps that use JWT instead of session). Also tries window.TqTagJWT and localStorage 'tqtag_jwt' if not provided.
   * @returns {Promise<Object|null>}
   */
  static async loadCurrentUser(jwtOrGetter = null) {
    try {
      const response = await API.getCurrentUser(jwtOrGetter);
      
      if (response.status === 'ok' && response.me) {
        return response.me;
      }
      
      return null;
    } catch (error) {
      // User not logged in or error - return null
      return null;
    }
  }

  /**
   * Validate form configuration structure
   * @param {Object} config - Form configuration
   * @returns {boolean}
   */
  static validate(config) {
    if (!config || typeof config !== 'object') {
      return false;
    }

    // Check required properties
    if (!config.uuid || !config.fields || !Array.isArray(config.fields)) {
      return false;
    }

    if (!config.pages || !Array.isArray(config.pages)) {
      return false;
    }

    if (!config.areas || !Array.isArray(config.areas)) {
      return false;
    }

    return true;
  }

  /**
   * Get form field by ID
   * @param {Object} config - Form configuration
   * @param {string} fieldId - Field ID
   * @returns {Object|null}
   */
  static getFieldById(config, fieldId) {
    if (!config || !config.fields) return null;
    return config.fields.find(field => field.id === fieldId) || null;
  }

  /**
   * Get form area by ID
   * @param {Object} config - Form configuration
   * @param {string} areaId - Area ID
   * @returns {Object|null}
   */
  static getAreaById(config, areaId) {
    if (!config || !config.areas) return null;
    return config.areas.find(area => area.id === areaId) || null;
  }

  /**
   * Get form page by ID
   * @param {Object} config - Form configuration
   * @param {string} pageId - Page ID
   * @returns {Object|null}
   */
  static getPageById(config, pageId) {
    if (!config || !config.pages) return null;
    return config.pages.find(page => page.id === pageId) || null;
  }

  /**
   * Get areas for a specific page
   * @param {Object} config - Form configuration
   * @param {string} pageId - Page ID
   * @returns {Array}
   */
  static getAreasForPage(config, pageId) {
    if (!config || !config.areas) return [];
    return config.areas.filter(area => area.page === pageId);
  }

  /**
   * Sort pages by position
   * @param {Array} pages - Pages array
   * @returns {Array}
   */
  static sortPages(pages) {
    if (!Array.isArray(pages)) return [];
    
    return [...pages].sort((a, b) => {
      const posA = a.poisition || a.position || 0;
      const posB = b.poisition || b.position || 0;
      return posA - posB;
    });
  }
}

